
























import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { getScrollTop, shouldLoadMore } from './utils/helpers';
import ScrollDirective from './directive/scroll-directive';

export interface CacheItem {
  offset: number;
  height: number;
}

export type GetItemSizeFn = (item: any, idx?: number) => number
export type GetItemSize = undefined | GetItemSizeFn

@Component({
  name: 'PVirtualPanel',
  directives: {
    scroll: ScrollDirective,
  },
})
export default class VirtualPanel extends Vue {
  /**
	 * 数据源
	 */
  @Prop({ required: true }) dataSource!: Array<any>;
	
  /**
	 * 面板高度
	 */
  @Prop({ required: true }) height!: number;
	
  /**
	 * 面板宽度
	 * @values number | string
	 */
  @Prop({ required: false, default: '100%' }) width!: number | string;
	
  /**
	 * 列表项高度
	 */
  @Prop({ required: true }) itemSize!: number;
	
  /**
	 * 动态获取列表项高度的方法
	 * @values (item: any, idx?: number) => number
	 */
  @Prop({ required: false }) getItemSize!: GetItemSize
	
  /**
   * 后面增加多少个元素 -> 优化白屏
   */
  @Prop({ required: false, default: 5 }) buffer!: number;
	
  /**
   * loadMore过程中，需要加锁，避免频繁 emit('load-more') 事件
   * 如果传入 load-more 事件，loading必传
   */
  @Prop({ required: false, default: false }) loading!: boolean;
	
  visibleData: any[] = [];
  lastMeasuredIndex = -1;
  startIndex = 0;
  cache: Record<string | number, CacheItem> = {};
	
  listEle!: HTMLElement;
  contentEle!: HTMLElement;
  footerHeight = 0;
  needLoadMore = false; // 是否需要监听滑动到底部并 loadMore
	
  get rootStyle(): Record<string, any> {
    return {
      height: `${this.height}px`,
      width: typeof this.width === 'number' ? `${this.width}px` : this.width,
    };
  }
	
  get contentHeight(): number {
    const { dataSource, lastMeasuredIndex } = this;
    const lastIdx = dataSource.length - 1;
    // 如果没有更多数据了，更正整体高度
    if (this.lastMeasuredIndex >= lastIdx + 1 && this.cache[lastIdx]) {
      const lastItem = this.dataSource[lastIdx]
      return this.cache[lastIdx].offset + this.getItemHeight(lastItem, lastIdx) + this.getFooterSlotHeight()
    }
		
    if (lastMeasuredIndex > 0) {
      const lastMeasured = this.getLastMeasuredSizeAndOffset();
      const { offset, height } = lastMeasured;
      const unCacheContentHeight = this.getUnCacheContentHeight(lastIdx - lastMeasuredIndex)
      return offset + height + unCacheContentHeight + this.footerHeight;
    }
    // 还没缓存过元素，直接取预估高度
    return this.getUnCacheContentHeight(0) + this.footerHeight;
  }
	
	
  @Watch('dataSource', { immediate: true, deep: true })
  onDataSourceChanged() {
    this.updateVisibleData();
  }
	
  mounted() {
    this.listEle = this.$refs.list as HTMLElement;
    this.contentEle = this.$refs.content as HTMLElement;
    this.handleSlots();
    this.handleListeners();
  }
	
  private onScroll(): void {
    requestAnimationFrame(() => {
      this.updateVisibleData();
      this.handleLoadMore();
    });
  }
	
  private updateVisibleData() {
    if (!this.listEle) {
      this.listEle = this.$refs.list as HTMLElement;
    }
    const scrollTop = getScrollTop(this.listEle);
    // 可见区域的起始数据索引， 已经滑过的高度scrollTop/item高度 -> 计算动态高度
    const start = this.getStartIdx(scrollTop);
    this.startIndex = start;
    // 可见区域的结束索引 -> 动态高度 + 可视区域高度
    const end = this.getStartIdx(scrollTop + this.height) + 1;
    this.setVisibleList(start, end);
    this.setContentPosition(start);
  }
	
  /**
   * 更新虚拟列表data
   */
  private setVisibleList(start: number, end: number): void {
    if (this.buffer > 0) {
      const bufferEnd = end + this.buffer > this.dataSource.length ? this.dataSource.length : end + this.buffer;
      this.visibleData = this.dataSource.slice(start, bufferEnd).map((item, idx) => {
        return {
          ...item,
          realIdx: idx + start,
        }
      })
    } else {
      this.visibleData = this.dataSource.slice(start, Math.min(end + 1, this.dataSource.length)).map((item, idx) => {
        return {
          ...item,
          realIdx: idx + start,
        }
      })
    }
  }
	
  /**
   * 设置列表滚动位置，需考虑buffer的start位置
   * @param start
   */
  private setContentPosition(start: number): void {
    if (!this.contentEle) {
      this.contentEle = this.$refs.content as HTMLElement;
    }
    if (!this.contentEle) return;
    const offset = this.getItemInfoByIdx(start).offset;
    // 滑到底，对齐高度
    const translateY = offset + this.height <= this.contentHeight ? offset : this.contentHeight - this.height;
    this.contentEle.style.transform = `translate3d(0, ${translateY}px, 0)`;
  }
	
  /**
   * 根据scrollTop找到当前可视区域的第一个元素
   */
  private getStartIdx(scrollTop: number): number {
    const lastMeasuredOffset = this.getLastMeasuredSizeAndOffset().offset;
    if (lastMeasuredOffset > scrollTop) {
      return this.BSSearch(0, this.lastMeasuredIndex, scrollTop);
    } else {
      return this.optimizedSearch(scrollTop);
    }
  }
	
  /**
   * 从cache中二分搜索
   */
  private BSSearch(low: number, high: number, offset: number): number {
    let index = 0;
		
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const middleOffset = this.getItemInfoByIdx(middle).offset;
      if (middleOffset === offset) {
        index = middle;
        break;
      } else if (middleOffset > offset) {
        high = middle - 1;
      } else {
        low = middle + 1;
      }
    }
		
    if (low > 0) {
      index = low - 1;
    }
		
    return index;
  }
	
  /**
   * 二分搜索进阶版
   */
  private optimizedSearch(scrollTop: number): number {
    let bound = 1;
    const { dataSource } = this;
    const start = this.lastMeasuredIndex >= 0 ? this.lastMeasuredIndex : 0;
    while (start + bound < dataSource.length && this.getItemInfoByIdx(start + bound).offset < scrollTop) {
      bound = bound * 2;
    }
    return this.BSSearch(start + Math.floor(bound / 2), Math.min(start + bound, dataSource.length), scrollTop);
  }
	
  /**
   * 根据index找到这个元素具体整个列表顶部的距离 -> 应用缓存减少计算量
   */
  private getItemInfoByIdx(index: number): CacheItem {
    const { lastMeasuredIndex, cache, dataSource } = this;
    if (lastMeasuredIndex >= index) {
      return cache[index];
    }
    let offset = 0;
    if (lastMeasuredIndex >= 0) {
      const lastMeasured = cache[lastMeasuredIndex];
      if (lastMeasured) {
        offset = lastMeasured.offset + lastMeasured.height;
      }
    }
    for (let i = lastMeasuredIndex + 1; i <= index; i++) {
      const item = dataSource[i];
      const height = this.getItemHeight(item, i);
      cache[i] = {
        height,
        offset,
      };
      offset += height;
    }
    if (index > lastMeasuredIndex) {
      this.lastMeasuredIndex = index;
    }
    return cache[index];
  }
	
  /**
   * 获取已经计算过高度的列表项的高度和
   */
  private getLastMeasuredSizeAndOffset(): CacheItem {
    return this.lastMeasuredIndex >= 0
      ? this.cache[this.lastMeasuredIndex]
      : {
        offset: 0,
        height: 0,
      };
  }
	
	
  /**
   * 未计算过的用itemSize * count 做预估
   * getItemHeight实时计算 -> long task 掉帧
   */
  private getUnCacheContentHeight(start: number): number {
    const list = this.dataSource.slice(start);
    // if (this.getItemSize) {
    //   return list.reduce((pre, cur, idx) => {
    //     return pre + this.getItemHeight(cur, idx + from)
    //   }, 0)
    // }
    return list.length * this.itemSize;
  }
	
  private getItemHeight(item: any, index: number): number {
    // 1、如果传入getItemSizeFn方法，用于动态计算高度
    // 2、固定itemSize
    // 3、此处暂不做计算几何位置信息 -> 理论上耗性能
    return this.getItemSize ? this.getItemSize(item, index) : this.itemSize;
  }
	
  /**
   * invoked by parent component -> update cache
   * 根据 高度diff 更新整个cache
   */
  public updateItemHeight(idx: number, height: number) {
    // 直接更新整个cache
    const cacheItem = this.cache[idx];
    const diff = height - cacheItem.height;
    this.cache[idx] = {
      ...this.cache[idx],
      height,
    }
    const cacheSize = Object.keys(this.cache).length;
    for (let i = idx + 1; i < cacheSize - 1; i++) {
      this.cache[i] = {
        ...this.cache[i],
        offset: this.cache[i].offset + diff,
      }
    }
    this.updateVisibleData();
  }
	
  private getFooterSlotHeight(): number {
    if (this.$slots && this.$slots.footer) {
      const footerEle = this.$refs.footer as HTMLElement;
      if (!footerEle) return 0;
      return footerEle.getBoundingClientRect().height;
    }
    return 0;
  }
	
  private handleSlots() {
    if (this.$slots && this.$slots.footer) {
      const footerEle = this.$refs.footer as HTMLElement;
      if (footerEle) {
        this.footerHeight = footerEle.getBoundingClientRect().height
      }
    }
  }
	
  private handleLoadMore(): void {
    if (!this.needLoadMore) return;
    if (this.loading) return;
    if (shouldLoadMore(this.listEle, this.contentHeight, this.height)) {
      /**
			 * 懒加载数据源
			 */
      this.$emit('load-more');
    }
  }
	
  private handleListeners(): void {
    if (this.$listeners && (this.$listeners['loadMore'] || this.$listeners['load-more'])) {
      this.needLoadMore = true;
    }
  }
}
